/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateModeration } from '../../../../../state/thunks/commenting';
// utils
import { url } from '../../../../../utils/urls';
// material
import {Switch, Group, IconControl, IncDecField} from '@insticator/insticator-ui'; 
import ConfigController from '../../../../../material/expandablesection/controllers/configcontroller/configcontroller'
import ExpandableSection from '../../../../../material/expandablesection/expandablesection'
import SectionDescription from '../../../../../material/expandablesection/sectiondescription/sectiondescription';
import SectionCard from '../../../../../material/expandablesection/sectioncard/sectioncard';
import SectionGroup from '../../../../../material/expandablesection/sectiongroup/sectiongroup';
import SectionGroupDescription from '../../../../../material/expandablesection/sectiongroupdescription/sectiongroupdescription'
// content
// styles
import injectSheet from 'react-jss';
import styles from './moderationsection_styles'

/* ========== ~~~~~~~~~~ COMMENTING CONFIG: MODERATION SECTION ~~~~~~~~~~ ========== */
const ModerationSection = injectSheet(styles)((props) => {
  const dispatch = useDispatch();

  const { mocked } = useSelector(state => state.session);
  const { 
    data: { moderation: configModeration }, 
    isUpdating 
  } = useSelector(state => state.commenting.commentingConfig)

  const initialModerationState = {
    ...configModeration,
    anonymousCommenting: configModeration.commentingUserType === 'ONLY_ANONYMOUSE_USER' || configModeration.commentingUserType === 'EVERY_USER',
    onlyAnonymousCommenting: configModeration.commentingUserType === 'ONLY_ANONYMOUSE_USER'
  }

  const [moderation, setModeration] = useState({...initialModerationState})
  const [isOpened, setIsOpened] = useState(false);


  const formatModerationType = (type) => type.split('_')[0].toLocaleLowerCase()

  const formatAnonymousPreview = type => ['ONLY_ANONYMOUSE_USER', 'EVERY_USER'].includes(type) ? 'ON' : 'OFF'

  const handleToggle = () => setIsOpened(!isOpened)

  const commentingUserType = () => {
    if(!moderation.anonymousCommenting){
      return 'ONLY_AUTHORIZED_USER'
    }
    else if(moderation.anonymousCommenting && moderation.onlyAnonymousCommenting){
      return 'ONLY_ANONYMOUSE_USER'
    }
    return 'EVERY_USER'
  }

  const handleSave = useCallback(async () => {
    const moderationData = {
      ...moderation,
      commentingUserType: commentingUserType()
    }
    const response = await dispatch(
      updateModeration({ moderationConfigurationUUID: configModeration.uuid, moderation: moderationData})
    )

    if(response.payload.data) setModeration(moderationData)
    else setModeration(initialModerationState)

    setIsOpened(false)
  })

  const handleCancel = () => {
    setIsOpened(false)
    setModeration({...initialModerationState})
  }

  const isChanged = useMemo(() => {
    const savedModerationState = {
      ...configModeration,
      anonymousCommenting: configModeration.commentingUserType === 'ONLY_ANONYMOUSE_USER' || configModeration.commentingUserType === 'EVERY_USER',
      onlyAnonymousCommenting: configModeration.commentingUserType === 'ONLY_ANONYMOUSE_USER'
    }

    const currentModerationState = {...moderation}

    const isFieldsChanged = !(JSON.stringify(currentModerationState) === JSON.stringify(savedModerationState));
    return isFieldsChanged
  }, [moderation, configModeration])

  const handleModerationConfigChange = (property, value, isAdvanced = false) => {
    if(isAdvanced)  {
      setModeration({
        ...moderation, 
        advancedSettings: {...moderation.advancedSettings, [property]: value }
      })
    }
    else setModeration({...moderation, [property]: value})
  }


  const controller = (
    <ConfigController
      isOpened={isOpened}
      handleToggle={handleToggle}
      handleCancel={handleCancel}
      handleSave={handleSave}
      isProcessing={isUpdating.moderation}
      disableSave={!isChanged}
      title='Moderation'
      description={<SectionDescription
          main={`${formatModerationType(configModeration.moderationType)}-moderation`}
          additional={`Anonymous commenting ${formatAnonymousPreview(configModeration.commentingUserType)} , +4 more`}
      />}
    />
  );

  return (
    <ExpandableSection
      className={`moderationsection ${props.classes.moderationsection}`}
      controller={controller}
      isOpened={isOpened}
      handleToggle={handleToggle}
      handleCancel={handleCancel}
      handleSave={handleSave}
      isProcessing={isUpdating.moderation}
      disableSave={!isChanged}
    >
      <SectionCard>
          <SectionGroup>
              <SectionGroupDescription
                  title={'Moderation type'}
                  // helpLink={'https://www.insticator.com/insticator-products/commenting/'}
              />
              <Group name={'controls'}>
                  <IconControl
                      text={'Post'}
                      selected={moderation.moderationType === 'POST_MODERATION'}
                      image={`${url.image}/illustrations/illustration-post-moderation.svg`}
                      value={'POST_MODERATION'}
                      handleSelect={value => handleModerationConfigChange('moderationType', value)}
                  />
                  <IconControl
                      text={'Pre'}
                      selected={moderation.moderationType === 'PRE_MODERATION'}
                      image={`${url.image}/illustrations/illustration-pre-moderation.svg`}
                      value={'PRE_MODERATION'}
                      handleSelect={value => handleModerationConfigChange('moderationType', value)}
                  />
                  <IconControl
                      text={'None'}
                      selected={moderation.moderationType === 'NO_MODERATION'}
                      image={`${url.image}/illustrations/illustration-no-moderation.svg`}
                      value={'NO_MODERATION'}
                      handleSelect={value => handleModerationConfigChange('moderationType', value)}
                  />
              </Group>
          </SectionGroup>

          <SectionGroup row>
              <SectionGroupDescription
                  title={'Anonymous commenting'}
                  description={'Unauthorized users can post comments.'}
                  // helpLink={'https://www.insticator.com/insticator-products/commenting/'}
              />
              <Switch
                  handleClick={() => handleModerationConfigChange('anonymousCommenting', !moderation.anonymousCommenting )}
                  on={moderation.anonymousCommenting}
                  variant='secondary'
              />
          </SectionGroup>
          { moderation.anonymousCommenting &&
          <SectionGroup row secondary>
              <SectionGroupDescription title='Allow only anonymous commenting'/>
              <Switch
                  on={moderation.onlyAnonymousCommenting}
                  handleClick={() => handleModerationConfigChange('onlyAnonymousCommenting', !moderation.onlyAnonymousCommenting )}
                  variant='secondary'
              />
          </SectionGroup>}
          <SectionGroup row>
              <SectionGroupDescription
                  title={'Rich media Comments'}
                  description={'Commenters can add rich media in comments (videos, images, GIFs).'}
                  // helpLink={'https://www.insticator.com/insticator-products/commenting/'}
              />
              <Switch
                  handleClick={() => handleModerationConfigChange('isMediaContentEnabled', !moderation.isMediaContentEnabled )}
                  on={moderation.isMediaContentEnabled}
                  variant='secondary'
              />
          </SectionGroup>
          {moderation.isMediaContentEnabled &&
          <SectionGroup row secondary>
              <SectionGroupDescription title='Embedded videos (from sites like YouTube, etc.)'/>
              <Switch
                  on={moderation.isVideosEnabled}
                  handleClick={() => handleModerationConfigChange('isVideosEnabled', !moderation.isVideosEnabled)}
                  variant='secondary'
              />
          </SectionGroup>}
          {moderation.isMediaContentEnabled &&
          <SectionGroup row secondary>
              <SectionGroupDescription title='Upload images'/>
              <Switch
                  on={moderation.isImagesEnabled}
                  handleClick={() => handleModerationConfigChange('isImagesEnabled', !moderation.isImagesEnabled)}
                  variant='secondary'
              />
          </SectionGroup>}
          {moderation.isMediaContentEnabled &&
          <SectionGroup row secondary>
              <SectionGroupDescription title='GIFs (from Giphy library)'/>
              <Switch
                  on={moderation.isGifsEnabled}
                  handleClick={() => handleModerationConfigChange('isGifsEnabled', !moderation.isGifsEnabled)}
                  variant='secondary'
              />
          </SectionGroup>}
          <SectionGroup row>
              <SectionGroupDescription
                  title={'Emoticon Comment Reactions'}
                  description={'Users can react to comments via emoticon reactions.'}
                  // helpLink={'https://www.insticator.com/insticator-products/commenting/'}
              />
              <Switch
                  handleClick={() => handleModerationConfigChange('isEmojiReactionsEnabled', !moderation.isEmojiReactionsEnabled)}
                  on={moderation.isEmojiReactionsEnabled}
                  variant='secondary'
              />
          </SectionGroup>
          <SectionGroup row>
              <SectionGroupDescription
                  title={'Auto block commenter'}
                  description={'Users can mark comments as spam. We will automatically block a commenter after 10 inappropriate marks.'}
                  // helpLink={'https://www.insticator.com/insticator-products/commenting/'}
              />
              <Switch
                  handleClick={() => handleModerationConfigChange('autoBlockAuthors', !moderation.autoBlockAuthors)}
                  on={moderation.autoBlockAuthors}
                  variant='secondary'
              />
          </SectionGroup>
          <SectionGroup row>
              <SectionGroupDescription
                  title={'Auto remove comments'}
                  description={'We will automatically remove a comment after 3 inappropriate marks.'}
                  // helpLink={'https://www.insticator.com/insticator-products/commenting/'}
              />
              <Switch
                  handleClick={() => handleModerationConfigChange('autoRemoveComments', !moderation.autoRemoveComments)}
                  on={moderation.autoRemoveComments}
                  variant='secondary'
              />
          </SectionGroup>
          <SectionGroup row>
              <SectionGroupDescription
                  title={'Block comments with links'}
                  description={'Comments can\'t contain links.'}
                  // helpLink={'https://www.insticator.com/insticator-products/commenting/'}
              />
              <Switch
                  handleClick={() => handleModerationConfigChange('blockCommentsWithLink', !moderation.blockCommentsWithLink)}
                  on={moderation.blockCommentsWithLink}
                  variant='secondary'
              />
          </SectionGroup>
      </SectionCard>
      {mocked &&
      <SectionCard title={'ADVANCED SETTINGS'}>
          <SectionGroup row>
              <SectionGroupDescription
                  title={'AI moderation'}
                  description={'Enable automatic AI moderation.'}
                  // helpLink={'https://www.insticator.com/insticator-products/commenting/'}
              />
              <Switch
                  handleClick={() => handleModerationConfigChange('isAIModerationEnabled', !moderation.advancedSettings.isAIModerationEnabled, true)}
                  on={moderation.advancedSettings.isAIModerationEnabled}
                  variant='secondary'
              />
          </SectionGroup>
          {moderation.advancedSettings.isAIModerationEnabled &&
          <div className='tablesectionview approvalthreshold'>
              <SectionGroup secondary>
                  <SectionGroupDescription title={'Min approval threshold'}/>
                  <IncDecField
                      name={'incdecfield'}
                      defaultValue={moderation.advancedSettings.minApprovalScore}
                      increment={1}
                      minValue={0}
                      maxValue={moderation.advancedSettings.maxApprovalScore}
                      handleIncToggle={value => handleModerationConfigChange('minApprovalScore', value, true)}
                      handleDecToggle={value => handleModerationConfigChange('minApprovalScore', value, true)}
                      ctaPosition={'inline'}
                  />
              </SectionGroup>
              <SectionGroup secondary>
                  <SectionGroupDescription title={'Max approval threshold'}/>
                  <IncDecField
                      name={'incdecfield'}
                      defaultValue={moderation.advancedSettings.maxApprovalScore}
                      increment={1}
                      minValue={moderation.advancedSettings.minApprovalScore}
                      maxValue={6}
                      handleIncToggle={value => handleModerationConfigChange('maxApprovalScore', value, true)}
                      handleDecToggle={value => handleModerationConfigChange('maxApprovalScore', value, true)}
                      ctaPosition={'inline'}
                  />
              </SectionGroup>
          </div>}

      </SectionCard>}
    </ExpandableSection>
  )
})  

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default ModerationSection;